/**
 * WorkerManager - Gestión del Web Worker desde el hilo principal
 * 
 * Intenta inicializar un Web Worker para procesar eventos en segundo plano.
 * Si el Worker no está disponible o falla, hace fallback automático a Dispatcher
 * en el hilo principal. Proporciona una API compatible con Dispatcher para
 * enqueue/flush, delegando al Worker cuando está activo.
 */

import Dispatcher from '../transport/dispatcher.js';

export class WorkerManager {
	constructor(config, options = {}) {
		this.config = config;
		this.worker = null;
		this.active = false;
		this.sensorIntegrity = options.sensorIntegrity || null;
		this.tokenManager = options.tokenManager || null;
		this.fallback = new Dispatcher(this.config, {
			sensorIntegrity: this.sensorIntegrity,
			tokenManager: this.tokenManager
		});
		this._fallbackStarted = false;
		this._readyPromise = null;
	}

	async init() {
		if (typeof Worker === 'undefined') {
			this.active = false;
			return;
		}

		const workerUrl = this.resolveWorkerUrl();
		if (!workerUrl) {
			this.active = false;
			return;
		}

		try {
			this.worker = new Worker(workerUrl, { type: 'module' });
		} catch (e) {
			this.worker = null;
			this.active = false;
			return;
		}

		// Esperar confirmación del worker con timeout de 5s
		this._readyPromise = new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				this.disableWorker();
				resolve(false);
			}, 5000);

			this.worker.onmessage = (evt) => {
				const msg = evt.data || {};
				if (msg.type === 'ready') {
					clearTimeout(timeoutId);
					this.active = true;
					resolve(true);
					return;
				}
				if (msg.type === 'error') {
					clearTimeout(timeoutId);
					this.disableWorker();
					resolve(false);
				}
			};

			this.worker.onerror = () => {
				clearTimeout(timeoutId);
				this.disableWorker();
				resolve(false);
			};
		});

		try {
			const configPayload = { ...this.config.getAll() };
			if (this.sensorIntegrity) {
				const sensorHash = await this.sensorIntegrity.getSensorHash();
				if (sensorHash) configPayload.sensorHash = sensorHash;
			}
			this.worker.postMessage({ type: 'init', config: configPayload });
		} catch (e) {
			this.disableWorker();
		}

		await this._readyPromise;
	}

	enqueue(eventType, detectedPayload, severity, meta = null) {
		const enrichedMeta = this.withOriginUrl(meta);

		// Si el worker está activo, enviar evento al worker; si no, usar fallback
		if (this.active && this.worker) {
			try {
				this.worker.postMessage({
					type: 'enqueue',
					events: [{ eventType, detectedPayload, severity, meta: enrichedMeta }]
				});
				return true;
			} catch (e) {
				this.disableWorker();
			}
		}

		return this.enqueueFallback(eventType, detectedPayload, severity, enrichedMeta);
	}

	flush() {
		if (this.active && this.worker) {
			try {
				this.worker.postMessage({ type: 'flush' });
				return Promise.resolve();
			} catch (e) {
				this.disableWorker();
			}
		}

		if (!this._fallbackStarted) {
			this.fallback.start();
			this._fallbackStarted = true;
		}
		return this.fallback.flush();
	}

	stop() {
		this.disableWorker();
		if (this._fallbackStarted) {
			this.fallback.stop();
		}
	}

	enqueueFallback(eventType, detectedPayload, severity, meta) {
		if (!this._fallbackStarted) {
			this.fallback.start();
			this._fallbackStarted = true;
		}
		return this.fallback.enqueue(eventType, detectedPayload, severity, meta);
	}

	disableWorker() {
		this.active = false;
		try {
			if (this.worker) this.worker.terminate();
		} catch (e) {
			// ignore
		}
		this.worker = null;
	}

	withOriginUrl(meta) {
		const originUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
		if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
			return { ...meta, origin_url: meta.origin_url || originUrl };
		}
		return { origin_url: originUrl };
	}

	resolveWorkerUrl() {
		// Intentar usar workerUrl configurado
		try {
			const configured = this.config && this.config.get ? this.config.get('workerUrl') : null;
			if (configured && typeof configured === 'string') {
				return configured;
			}
		} catch (e) {
		}

		if (typeof document === 'undefined') return null;

		// Resolver URL relativa desde el script actual o buscar script adr-sensor
		const current = document.currentScript && document.currentScript.src ? document.currentScript.src : '';
		if (current) {
			return new URL('adr-worker.js', current).toString();
		}

		const scripts = document.getElementsByTagName('script');
		for (let i = scripts.length - 1; i >= 0; i--) {
			const src = scripts[i] && scripts[i].src ? scripts[i].src : '';
			if (src && src.indexOf('adr-sensor') !== -1) {
				return new URL('adr-worker.js', src).toString();
			}
		}

		return null;
	}
}

export default WorkerManager;


